import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";

import { api } from "../lib/api";
import { BRAND } from "../lib/brand";
import { money } from "../lib/format";
import type { CurrentUser } from "../lib/types";
import { Wordmark } from "./Mark";
import { ReportIssue } from "./ReportIssue";

type Theme = "light" | "dark" | "system";

function useTheme() {
  const [theme, setTheme] = useState<Theme>(() => {
    const stored = localStorage.getItem("ca-theme");
    return stored === "light" || stored === "dark" ? stored : "system";
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "system") {
      root.removeAttribute("data-theme");
      localStorage.removeItem("ca-theme");
    } else {
      root.setAttribute("data-theme", theme);
      localStorage.setItem("ca-theme", theme);
    }
  }, [theme]);

  return [theme, setTheme] as const;
}

const NAV = [
  // `/` is the choice of work — the first thing after signing in — so the run
  // list has a route of its own rather than being the index.
  { to: "/", label: "Start", end: true },
  { to: "/engagements", label: "Engagements", end: false },
  { to: "/projects", label: "Projects", end: false },
  // The route keeps its name — it is the API resource, and it is bookmarked —
  // but the page holds data rooms *and* expert interviews now, so the label
  // has to cover both.
  { to: "/data-rooms", label: "Documents", end: false },
  // What the knowledge graph holds on a topic, as a briefing — every user's,
  // reading only what they may see (#135).
  { to: "/memory", label: "Memory", end: false },
];

export function Layout() {
  const [theme, setTheme] = useTheme();
  const health = useQuery({
    queryKey: ["status"],
    queryFn: api.status,
    refetchInterval: 30_000,
  });
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });
  //     A playbook's form is the second step of Start, so Start stays lit
  //     there — it is `end`, and nothing in the bar was active on /new/cdd.
  const { pathname } = useLocation();
  const startActive = pathname === "/" || pathname.startsWith("/new/");

  //     Below `lg` the bar is the wordmark, the balance and a menu. It used to
  //     keep every control in one row that neither wrapped nor collapsed, so
  //     at 390px the document was 915px wide and the theme switch, balance
  //     and sign-out were reachable only by scrolling sideways (#124).
  const [menu, setMenu] = useState(false);
  useEffect(() => setMenu(false), [pathname]);

  const items = [
    ...NAV,
    ...(me.data?.role === "admin" ? [{ to: "/admin", label: "Admin", end: false }] : []),
  ];
  const navClass = (isActive: boolean, to: string, stacked = false) =>
    `rounded-lg px-3 text-sm transition-colors ${stacked ? "block py-1.5" : "flex h-control-sm items-center"} ${
      isActive || (to === "/" && startActive)
        ? "bg-surface-2 font-medium text-ink"
        : "text-ink-2 hover:bg-surface-2 hover:text-ink"
    }`;

  return (
    <div className="min-h-dvh">
      <header className="sticky top-0 z-20 border-b border-line bg-surface/85 backdrop-blur">
        <div className="mx-auto flex max-w-page items-center gap-6 px-5 py-3">
          <NavLink to="/" className="flex shrink-0 items-center gap-3">
            <Wordmark />
            <span className="label hidden 2xl:inline">
              {BRAND.tagline}
            </span>
          </NavLink>

          {/*
            Admin is appended rather than hidden with CSS: the server gates
            /api/admin/* on the role regardless, so this is about not
            offering a page that would only 403 — not about secrecy.
          */}
          <nav className="hidden items-center gap-1 lg:flex" aria-label="Main">
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => navClass(isActive, item.to)}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/*
            The theme is set once and left; it does not need the bar's room
            on every page, so it lives in the account menu with the one other
            thing a person sets about themselves.
          */}
          <div className="ml-auto hidden items-center gap-3 lg:flex">
            <ReportIssue />
            <LiveRuns count={health.data?.live_runs.length ?? 0} />
            {me.data && <AccountMenu user={me.data} theme={theme} setTheme={setTheme} />}
          </div>

          <div className="ml-auto flex items-center gap-3 lg:hidden">
            {me.data && (
              <NavLink to="/billing" className="font-mono text-xs tabular-nums text-ink">
                {money(me.data.available_usd)}
              </NavLink>
            )}
            <button
              type="button"
              onClick={() => setMenu((open) => !open)}
              aria-expanded={menu}
              aria-controls="site-menu"
              className="flex h-control-sm items-center gap-1.5 rounded-lg border border-line px-2.5 text-sm text-ink-2 hover:bg-surface-2 hover:text-ink"
            >
              <svg
                aria-hidden
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              >
                {menu ? (
                  <path d="M3 3l8 8M11 3l-8 8" />
                ) : (
                  <path d="M2 4h10M2 7h10M2 10h10" />
                )}
              </svg>
              Menu
            </button>
          </div>
        </div>

        {menu && (
          <div id="site-menu" className="border-t border-line bg-surface lg:hidden">
            <nav className="mx-auto max-w-page space-y-1 px-5 py-3" aria-label="Main">
              {items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) => navClass(isActive, item.to, true)}
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
            <div className="mx-auto flex max-w-page flex-wrap items-center gap-3 border-t border-line px-5 py-3">
              <ThemeSwitch theme={theme} setTheme={setTheme} />
              <LiveRuns count={health.data?.live_runs.length ?? 0} />
              <ReportIssue />
              {me.data && (
                <button
                  type="button"
                  onClick={signOut}
                  className="ml-auto text-sm text-ink-2 underline underline-offset-4 hover:text-ink"
                >
                  Sign out {me.data.email}
                </button>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="mx-auto max-w-page px-5 py-6">
        <Outlet />
      </main>
    </div>
  );
}

async function signOut() {
  await api.logout();
  window.location.replace("/login");
}

function ThemeSwitch({ theme, setTheme }: { theme: Theme; setTheme: (t: Theme) => void }) {
  return (
    <div className="flex items-center rounded-lg border border-line p-0.5" role="group" aria-label="Colour theme">
      {(["light", "system", "dark"] as const).map((value) => (
        <button
          key={value}
          type="button"
          onClick={() => setTheme(value)}
          aria-pressed={theme === value}
          className={`rounded-sm px-2 py-1 text-xs capitalize transition-colors ${
            theme === value ? "bg-surface-2 font-medium text-ink" : "text-muted hover:text-ink"
          }`}
        >
          {value}
        </button>
      ))}
    </div>
  );
}

function LiveRuns({ count: live }: { count: number }) {
  if (live === 0) return null;
  return (
    <span className="flex items-center gap-1.5 text-xs text-ink-2">
      <span className="size-1.5 animate-pulse rounded-full bg-good" aria-hidden />
      <span className="font-mono tabular-nums">{live}</span> live
    </span>
  );
}

/**
 * Who is signed in, what they have left, and the two things they set about
 * themselves.
 *
 * The balance sits in the header rather than on a settings page because it is
 * the number that decides whether the next engagement can start — finding that
 * out at the moment the button is pressed is too late.
 *
 * The avatar used to *be* the sign-out: one click on your own initial ended
 * the session, with the email in a tooltip as the only warning. It opens a
 * menu now — who you are, the theme, and signing out as a choice you read
 * before you make it.
 */
function AccountMenu({
  user,
  theme,
  setTheme,
}: {
  user: CurrentUser;
  theme: Theme;
  setTheme: (t: Theme) => void;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  return (
    <div className="flex items-center gap-3">
      <NavLink to="/billing" className="hidden text-right leading-tight sm:block">
        <div className="font-mono text-xs tabular-nums text-ink hover:underline">
          {money(user.available_usd)}
        </div>
        {user.held_usd > 0 && (
          <div className="label">{money(user.held_usd)} reserved</div>
        )}
      </NavLink>
      <div className="relative" ref={box}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-haspopup="true"
          aria-label={`Account: ${user.email}`}
          className="flex size-control-sm items-center justify-center overflow-hidden rounded-full border border-line text-xs font-medium uppercase text-ink-2 transition-colors hover:bg-surface-2 hover:text-ink"
        >
          {user.avatar_url ? (
            <img src={user.avatar_url} alt="" className="size-full object-cover" />
          ) : (
            (user.name || user.email).slice(0, 1)
          )}
        </button>
        {open && (
          <div className="card absolute right-0 top-full z-30 mt-2 w-64 space-y-3 p-4 shadow-lg">
            <div className="min-w-0">
              {user.name && <p className="truncate text-sm font-medium text-ink">{user.name}</p>}
              <p className="truncate font-mono text-xs text-muted">{user.email}</p>
            </div>
            <div>
              <p className="label mb-1.5">Theme</p>
              <ThemeSwitch theme={theme} setTheme={setTheme} />
            </div>
            <button
              type="button"
              onClick={signOut}
              className="text-sm text-ink-2 underline underline-offset-4 hover:text-ink"
            >
              Sign out
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
